<script setup>
import { computed, watch, onMounted } from "vue";
import { useGlobalStore } from "@/stores/global";
import { useShopifyStore } from "@/stores/shopify";
import { useRoute } from "vue-router";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { prefersReducedMotion } from "@/utils/prefersReducedMotion";
import Product from "@/components/cards/Product.vue";
import ArrowLeft from "@/components/icons/ArrowLeft.vue";

const route = useRoute();
const shopify = useShopifyStore();
const global = useGlobalStore();

const collection = computed(
	() => shopify.collections.find((c) => c.slug === route.params.slug) ?? null,
);

const products = computed(() => {
	if (!collection.value?.products) return [];

	return collection.value.products;
});

const layout = [
	{
		type: "product",
		index: 0,
		classes: "lg:col-span-1 col-span-2 lg:!aspect-auto !aspect-[2/1] ",
	},
	{ type: "image", key: "one", classes: "aspect-square" },
	{ type: "product", index: 1 },
	{ type: "product", index: 2, classes: "lg:col-span-2 lg:!aspect-[2/1]" },
	{
		type: "image",
		key: "two",
		classes: "lg:row-span-2 lg:aspect-auto aspect-square",
	},
	{ type: "product", index: 3 },
	{ type: "image", key: "three", classes: "aspect-square" },
	{ type: "image", key: "four", classes: "aspect-square" },
	{ type: "product", index: 4, classes: "lg:col-span-2 lg:!aspect-auto" },
];

const currentLayout = computed(() => {
	const productCount = products.value.length;
	const hasFeaturedImages = collection.value?.featured_images;

	if (productCount < 2) {
		return null;
	}

	const hasAllImages =
		hasFeaturedImages?.one &&
		hasFeaturedImages?.two &&
		hasFeaturedImages?.three &&
		hasFeaturedImages?.four;

	const useStructuredLayout = productCount >= 5 && hasAllImages;

	if (useStructuredLayout) {
		let baseLayout = layout.filter((item) => {
			if (item.type === "product") {
				return products.value[item.index];
			}
			if (item.type === "image") {
				return hasFeaturedImages?.[item.key];
			}
			return true;
		});

		const maxProductIndex = Math.max(
			...baseLayout
				.filter((item) => item.type === "product")
				.map((item) => item.index),
		);

		const remainingProducts = [];
		for (let i = maxProductIndex + 1; i < productCount; i++) {
			remainingProducts.push({
				type: "product",
				index: i,
				classes: "aspect-square",
			});
		}

		return [...baseLayout, ...remainingProducts];
	} else {
		const simpleLayout = [];
		for (let i = 0; i < productCount; i++) {
			simpleLayout.push({
				type: "product",
				index: i,
			});
		}
		return simpleLayout;
	}
});

const renderLayoutItem = (item) => {
	if (item.type === "product") {
		const product = products.value[item.index];
		if (!product) return null;

		return {
			component: Product,
			props: { product },
			classes: `${item.classes || ""} collection-item`,
		};
	}

	if (item.type === "image") {
		const baseClasses = "rounded-lg bg-cover bg-center";

		const imageUrl = collection.value?.featured_images?.[item.key];
		if (!imageUrl) return null;

		return {
			component: "div",
			props: {},
			classes: `${baseClasses} ${item.classes || ""} collection-item`,
			style: {
				backgroundImage: `url(${imageUrl})`,
			},
		};
	}

	return null;
};

watch(
	collection,
	(collection) => {
		if (collection) {
			global.setTheme(
				collection.theme?.background,
				collection.theme?.accent,
				collection.theme?.surface,
			);
		}
	},
	{ immediate: true },
);

onMounted(() => {
	if (prefersReducedMotion()) return;

	gsap.registerPlugin(ScrollTrigger);

	const collectionItems = gsap.utils.toArray(".collection-item");

	collectionItems.forEach((item) => {
		gsap.from(item, {
			css: {
				clipPath: "inset(0% 0% 0% 100%)",
				opacity: 0,
				transform: "translateY(24px) scale(0.9)",
			},
			duration: 1.0,
			ease: "power2.inOut",
			scrollTrigger: {
				trigger: item,
				start: "top 80%",
				end: "bottom top",
			},
		});
	});
});
</script>

<template>
	<section
		class="flex flex-col items-center justify-center gap-24 pt-32 pb-48 lg:gap-40 lg:pt-60 lg:pb-120"
	>
		<router-link
			class="flex items-center gap-8 transition-opacity hover:opacity-75"
			:to="{ name: 'index' }"
		>
			<ArrowLeft />
			<p class="h8 font-primary-condensed">See all teams</p>
		</router-link>
		<img
			:src="collection?.logo"
			:alt="collection?.title"
			class="h-48 w-auto object-contain md:h-64 lg:h-96"
		/>
	</section>
	<section class="pb-48">
		<div class="container">
			<div class="grid grid-cols-2 gap-8 lg:grid-cols-3">
				<template v-if="currentLayout">
					<component
						v-for="(item, index) in currentLayout"
						:key="index"
						:is="renderLayoutItem(item)?.component"
						v-bind="renderLayoutItem(item)?.props || {}"
						:class="renderLayoutItem(item)?.classes"
						:style="renderLayoutItem(item)?.style"
					/>
				</template>

				<template v-else>
					<Product
						v-for="product in products"
						:key="product.id"
						:product="product"
					/>
				</template>
			</div>
		</div>
	</section>
</template>
